import {
    Configuration,
    OrderApi,
    ReturnApi,
    DecisionCodeApi,
    ConfigurationApi,
    SkuLocationApi,
    B2CQueueApi,
} from 'returnCenter-api';
import { getMsalInstance } from './msalInstanceHolder';
import { getScopeMap } from 'auth-config';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import type { RootState } from 'store';

// Store is injected at startup to avoid circular dependency (api ↔ store).
type AppStore = { getState: () => RootState; dispatch: (...args: unknown[]) => unknown };

let _store: AppStore | null = null;

export function injectStore(store: AppStore) {
    _store = store;
}

type headers = {
    accessToken?: string;
    scopes: string[];
};

// Flag to prevent multiple redirect attempts
let isRedirecting = false;

const forceReauthenticate = async () => {
    if (isRedirecting) return;
    isRedirecting = true;

    const msalInstance = getMsalInstance();
    const scopes = getScopeMap().returnCenter.scopes;

    try {
        await msalInstance.loginRedirect({
            scopes,
            prompt: 'login', // Force the user to enter credentials again
        });
    } catch (error) {
        console.error('Force reauthentication failed:', error);
        // As fallback, redirect to login
        try {
            await msalInstance.loginRedirect({
                scopes,
            });
        } catch (fallbackError) {
            console.error('Fallback reauthentication failed:', fallbackError);
            isRedirecting = false;
        }
    }
};

const TOKEN_ERRORS = [
    'refresh token',
    'invalid_grant',
    'interaction_required',
    'token expired',
    '50076', // MFA required
    '50079', // MFA required
    '50173', // Credential expired
    '700082', // Refresh token expired
    '700084', // Refresh token expired for SPA
    'aadsts700084',
    'aadsts700082',
    'aadsts50173',
    'aadsts50076',
    'aadsts50079',
];

const isTokenExpiredError = (error: unknown): boolean => {
    const errorMessage = (error as any)?.message?.toLowerCase() || '';
    const errorCode = (error as any)?.errorCode?.toLowerCase() || '';

    return TOKEN_ERRORS.some((err) => errorMessage.includes(err) || errorCode.includes(err));
};

export const getAccessToken = async ({ accessToken, scopes }: headers) => {
    const msalInstance = getMsalInstance();
    if (!accessToken) {
        const account = msalInstance.getActiveAccount();
        if (!account) {
            throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                scopes,
                account,
            });
            accessToken = response.accessToken;
        } catch (error) {
            if (
                error instanceof InteractionRequiredAuthError ||
                error instanceof BrowserAuthError ||
                isTokenExpiredError(error)
            ) {
                console.warn('Token refresh failed, forcing reauthentication:', error);
                await forceReauthenticate();
                throw error;
            } else {
                console.error('Error during acquireTokenSilent:', error);
                toast.error(
                    'An error occurred while acquiring token. Please try again.or contact Service-Desk if the issue persists.',
                );
                throw error;
            }
        }
    }
    return accessToken;
};

const isAuthError = (error: AxiosError): boolean => {
    const status = error.response?.status;
    const responseData = error.response?.data as any;

    // for 400, we need to check if it's an auth error or just a bad request
    if (status && status < 500 && responseData) {
        // Check error_codes in response body
        const errorCodes: string[] | number[] = responseData.error_codes || [];
        if (errorCodes.some((code) => TOKEN_ERRORS.includes(code.toString()))) {
            return true;
        }

        // Check error message in response body
        const rawError = responseData.error;
        const errorMessage: string = typeof rawError === 'string' ? rawError : '';
        if (errorMessage && TOKEN_ERRORS.some((err) => errorMessage.toLowerCase().includes(err))) {
            return true;
        }
    }

    return false;
};

// Create axios instance with interceptors for OrderApi
export const orderAxiosInstance = axios.create({
    baseURL: window.location.origin,
});

orderAxiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const scopes = getScopeMap().returnCenter.scopes;
        const accessToken = await getAccessToken({ scopes });

        if (!accessToken && isRedirecting) {
            throw new axios.Cancel('Redirecting for authentication');
        }

        const { selectedCompany: companyId, selectedMarketId: marketId } = _store!.getState().companies;

        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers.Accept = 'application/json';

        // Let axios set Content-Type automatically for FormData (with correct boundary)
        // Otherwise default to JSON
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        } else if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }

        config.headers['X-Company-Id'] = companyId;
        if (marketId != null) {
            config.headers['X-Market-Id'] = marketId.toString();
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

orderAxiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        if (isAuthError(error)) {
            console.warn(
                'Auth error in response, forcing reauthentication:',
                error.response?.status,
                error.response?.data,
            );
            await forceReauthenticate();
        }
        return Promise.reject(error);
    },
);

const apiConfig = new Configuration({ basePath: window.location.origin });

export const orderApi = new OrderApi(apiConfig, undefined, orderAxiosInstance as any);
export const returnApi = new ReturnApi(apiConfig, undefined, orderAxiosInstance as any);
export const decisionCodeApi = new DecisionCodeApi(apiConfig, undefined, orderAxiosInstance as any);
export const configurationApi = new ConfigurationApi(apiConfig, undefined, orderAxiosInstance as any);
export const skuLocationApi = new SkuLocationApi(apiConfig, undefined, orderAxiosInstance as any);
export const b2cQueueApi = new B2CQueueApi(apiConfig, undefined, orderAxiosInstance as any);

export default orderAxiosInstance;
